import { OrbitControls, PerspectiveCamera } from "@react-three/drei";
import { Canvas } from "@react-three/fiber";
import { useEffect, useMemo, useRef, useState } from "react";
import { BasketballCourt } from "./BasketballCourt";
import { CameraPreset } from "./courtConstants";
import { normalizeToWorldSpace, PIXEL_COURT_WIDTH } from "./courtUtils";
import { HeatMapDot } from "./HeatMapDot";
import { HeatMapToolbar } from "./HeatMapToolbar";
import LogoMesh from "./LogoMesh";
import { RecordingScene } from "./RecordingScene";
import { RegionDetailView } from "./RegionDetailView";
import { RegionStatsProcessor } from "./RegionStatsProcessor";
import { RegionView } from "./RegionView";
import { RegionViewBottom } from "./RegionViewBottom";
import { SharedAnimationScene } from "./SharedAnimationScene";
import { SpotViewBottom } from "./SpotViewBottom";

export const HeatMapLite = ({ shots, containerHeight, onlyMap = false }) => {
	const [cameraPerspective, setCameraPerspective] = useState("TOP");
	const [animationState, setAnimationState] = useState("stopped"); // 'stopped', 'playing', 'paused'
	const [zoomLevel, setZoomLevel] = useState(1);
	const [selectedRegion, setSelectedRegion] = useState(null);
	const [regionStats, setRegionStats] = useState([]);
	const [selectedRegionDetail, setSelectedRegionDetail] = useState(null);

	const handleRegionStatsCalculated = (data) => {
		if (data) {
			setRegionStats(data.stats);
		}
	};

	const handleRegionClick = (index) => {
		//console.log("Region clicked:", index);
		if (regionStats?.[index]) {
			//console.log("Region name:", regionStats[index].name);
			// Open the detail view for this region
			setSelectedRegionDetail(regionStats[index]);
		}
		setSelectedRegion((prev) => (prev === index ? null : index));
	};

	const handleRegionDetailClick = (region) => {
		setSelectedRegionDetail(region);
	};

	const handleCloseRegionDetail = () => {
		setSelectedRegionDetail(null);
	};

	// Enhanced zoom change handler with bounds
	const handleZoomChange = (newZoomLevel) => {
		const clampedZoom = Math.max(0.8, Math.min(1.5, newZoomLevel));
		setZoomLevel(clampedZoom);
	};
	const [sideFilter, setSideFilter] = useState("left"); // 'left' | 'right'
	const [restartKey, setRestartKey] = useState(0);
	const [completedCount, setCompletedCount] = useState(0);
	const [spotView, setSpotView] = useState(true); // Default to showing heatmap during animation
	const [cameraAngle, setCameraAngle] = useState(3);
	const [isFullscreen, setIsFullscreen] = useState(false);
	const [isRecording, setIsRecording] = useState(false);
	const containerRef = useRef();
	const canvasRef = useRef();

	const [initiateRecording, setInitiateRecording] = useState(false);

	const camera = CameraPreset[cameraPerspective];

	// Calculate shooting statistics
	const shootingStats = useMemo(() => {
		if (!shots || shots.length === 0) {
			return {
				FGPercentage: 0,
				FGMade: 0,
				FGTotal: 0,
				TPPercentage: 0,
				TPMade: 0,
				TPTotal: 0,
				FTPercentage: 0,
				FTMade: 0,
				FTTotal: 0,
			};
		}

		const FGMade = shots.filter(
			(shot) =>
				shot.field_goal_position === "2PFG" &&
				shot.field_goal_result === "score",
		).length;
		const FGTotal = shots.filter(
			(shot) => shot.field_goal_position === "2PFG",
		).length;

		const TPMade = shots.filter(
			(shot) =>
				shot.field_goal_position === "3PFG" &&
				shot.field_goal_result === "score",
		).length;
		const TPTotal = shots.filter(
			(shot) => shot.field_goal_position === "3PFG",
		).length;

		// Assuming free throws are not in the data, so defaulting to 0

		const FTMade = shots.filter(
			(shot) =>
				shot.field_goal_position === "FT" && shot.field_goal_result === "score",
		).length;
		const FTTotal = shots.filter(
			(shot) => shot.field_goal_position === "FT",
		).length;

		return {
			FGPercentage: FGTotal > 0 ? Math.round((FGMade / FGTotal) * 100) : 0,
			FGMade,
			FGTotal,
			TPPercentage: TPTotal > 0 ? Math.round((TPMade / TPTotal) * 100) : 0,
			TPMade,
			TPTotal,
			FTPercentage: FTTotal > 0 ? Math.round((FTMade / FTTotal) * 100) : 0,
			FTMade,
			FTTotal,
		};
	}, [shots]);

	// The center of the basketball court
	const orbitTarget = [0, 0, 0];

	const filteredShots = useMemo(() => {
		const allShots = shots || [];
		return allShots.filter((shot) => {
			const side = shot.left_or_right;
			if (sideFilter === "left" || shot.pixelX < PIXEL_COURT_WIDTH / 2)
				return side === "left";
			if (sideFilter === "right" || shot.pixelX >= PIXEL_COURT_WIDTH / 2)
				return side === "right";
			return true;
		});
	}, [shots, sideFilter]);

	const handleTogglePlayPause = () => {
		if (animationState === "playing") {
			setAnimationState("paused");
		} else {
			if (animationState === "stopped") {
				setCompletedCount(0);
			}
			setAnimationState("playing");
		}
	};

	const handleRestart = () => {
		setAnimationState("stopped");
		setCompletedCount(0);
		setRestartKey((prev) => prev + 1);
		// Start playing shortly after to allow components to re-mount
		setTimeout(() => {
			setAnimationState("playing");
		}, 50);
	};

	const handleFullscreen = async () => {
		if (containerRef.current) {
			if (document.fullscreenElement) {
				// Exit fullscreen
				await document.exitFullscreen();
				setIsFullscreen(false);

				// Unlock screen orientation
				if (window.screen.orientation?.unlock) {
					try {
						window.screen.orientation.unlock();
					} catch (_error) {
						//console.log("Orientation unlock not supported or failed:", error);
					}
				}
			} else {
				// Enter fullscreen
				try {
					await containerRef.current.requestFullscreen();
					setIsFullscreen(true);

					// Lock to landscape orientation for mobile
					if (window.screen.orientation?.lock) {
						try {
							await window.screen.orientation.lock("landscape");
							//console.log("Locked to landscape orientation");
						} catch (error) {
							console.log(
								"Landscape orientation lock not supported or failed:",
								error,
							);
						}
					}
				} catch (error) {
					console.error("Fullscreen request failed:", error);
				}
			}
		}
	};

	const handleToggleRecording = () => {
		if (!initiateRecording) {
			setIsRecording(true); // Provide immediate feedback to the user
			setInitiateRecording(true); // Mount the recording component to start the process
		}
	};

	const handleRecordingComplete = () => {
		setInitiateRecording(false);
		setIsRecording(false);
	};

	const cameraPerspectives = [
		"FRONT",
		"FRONT_RIGHT",
		"RIGHT",
		"TOP",
		"LEFT",
		"FRONT_LEFT",
	];
	const toggleCameraPerspective = () => {
		// Prevent camera switching during animation
		if (animationState !== "stopped") return;

		setCameraPerspective((prev) => {
			const currentIndex = cameraPerspectives.indexOf(prev);
			const nextIndex = (currentIndex + 1) % cameraPerspectives.length;
			setCameraAngle(nextIndex);
			return cameraPerspectives[nextIndex];
		});
	};

	const toggleSide = () => {
		setSideFilter((prev) => (prev === "left" ? "right" : "left"));
	};

	const toggleSpotView = () => {
		// Prevent view switching during animation
		if (animationState !== "stopped") return;

		setSpotView((prev) => !prev);
	};

	const toggleCameraAngle = (direction) => {
		// Prevent camera switching during animation
		if (animationState !== "stopped") return;

		let nextAngle;
		if (direction === "prev") {
			nextAngle =
				(cameraAngle - 1 + cameraPerspectives.length) %
				cameraPerspectives.length;
		} else {
			nextAngle = (cameraAngle + 1) % cameraPerspectives.length;
		}
		setCameraAngle(nextAngle);
		setCameraPerspective(cameraPerspectives[nextAngle]);
	};

	// Handle fullscreen changes (ESC key, etc.)
	useEffect(() => {
		const handleFullscreenChange = () => {
			setIsFullscreen(!!document.fullscreenElement);

			// If exiting fullscreen, unlock orientation
			if (
				!document.fullscreenElement &&
				window.screen.orientation &&
				window.screen.orientation.unlock
			) {
				try {
					window.screen.orientation.unlock();
				} catch (_error) {
					//console.log("Orientation unlock not supported or failed:", error);
				}
			}
		};

		document.addEventListener("fullscreenchange", handleFullscreenChange);

		return () => {
			document.removeEventListener("fullscreenchange", handleFullscreenChange);
		};
	}, []);

	// Lock camera to FRONT view when animation starts
	useEffect(() => {
		if (animationState === "playing" && cameraPerspective !== "FRONT") {
			setCameraPerspective("FRONT");
			setCameraAngle(0); // FRONT is at index 0
		}
	}, [animationState, cameraPerspective]);

	useEffect(() => {
		if (
			animationState !== "stopped" &&
			filteredShots.length > 0 &&
			completedCount >= filteredShots.length
		) {
			// Wait 2 seconds after animation completes to show stats before stopping
			const timer = setTimeout(() => {
				setAnimationState("stopped");
			}, 4000);

			return () => clearTimeout(timer);
		}
	}, [completedCount, filteredShots.length, animationState]);

	const viewMode = animationState === "stopped" ? "HEATMAP" : "ANIMATION";

	if (onlyMap) {
		return (
			<div
				ref={containerRef}
				className="relative w-full h-full min-h-0 bg-gradient-to-b from-blue-900 to-blue-700 overflow-hidden"
				style={{ height: containerHeight || "555px" }}
			>
				<Canvas
					shadows
					ref={canvasRef}
					gl={{
						toneMapping: 0,
						antialias: true,
						powerPreference: "high-performance",
						alpha: false,
						stencil: false,
						logarithmicDepthBuffer: true,
					}}
					dpr={[1, 2]}
				>
					<ambientLight intensity={0.4} />
					<directionalLight
						position={[10, 20, 10]}
						intensity={0.5}
						castShadow
						shadow-mapSize-width={2048}
						shadow-mapSize-height={2048}
						shadow-camera-far={50}
						shadow-camera-left={-20}
						shadow-camera-right={20}
						shadow-camera-top={20}
						shadow-camera-bottom={-20}
					/>
					<group key={`${cameraPerspective}-HEATMAP`}>
						<PerspectiveCamera
							makeDefault
							position={camera.position}
							rotation={camera.rotation}
							fov={camera.fov}
							near={camera.near}
							far={camera.far}
							zoom={camera.zoom}
						/>
						<OrbitControls
							target={orbitTarget}
							minDistance={3}
							maxDistance={30}
							maxPolarAngle={Math.PI * 0.85}
							enablePan={false}
							enableZoom={false}
							enableRotate={false}
						/>
					</group>
					<BasketballCourt />
					<mesh position={[0, -0.01, 0]} rotation={[-Math.PI / 2, 0, 0]}>
						<planeGeometry args={[1000, 1000]} />
						<meshStandardMaterial color="#031422" metalness={0} roughness={0} />
					</mesh>
					{filteredShots?.map((shot, index) => {
						if (!shot.location) return null;
						const worldLocation = normalizeToWorldSpace(shot.location);
						return (
							<HeatMapDot
								key={`heatmap-${shot.id || index}`}
								position={[worldLocation.x, 0.1, worldLocation.z]}
								userData={shot}
							/>
						);
					})}
					<LogoMesh />
				</Canvas>
			</div>
		);
	}

	return (
		<div className="flex flex-col w-full h-full min-h-0">
			{/* Fullscreen mobile styles */}
			<style>{`
        @media screen and (max-width: 768px) {
          :fullscreen {
            width: 100vw !important;
            height: 100vh !important;
          }

          /* Hide browser UI in fullscreen on mobile */
          :fullscreen .mobile-fullscreen-hide {
            display: none;
          }

          /* Landscape orientation styles */
          @media screen and (orientation: landscape) and (max-height: 500px) {
            :fullscreen .mobile-landscape-adjust {
              padding: 0;
              margin: 0;
            }
          }
        }
      `}</style>

			<div
				className="flex flex-col min-h-0 flex-grow"
				style={{ fontFamily: '"SF Pro"' }}
			>
				{/* Main HeatMap Component */}
				<div
					ref={containerRef}
					className={`relative mx-auto bg-gradient-to-b from-blue-900 to-blue-700 overflow-hidden min-h-0 flex-grow transition-all duration-300 ${
						isFullscreen ? "w-screen h-screen rounded-none" : "w-full"
					}`}
					style={
						!isFullscreen ? { height: containerHeight || "555px" } : undefined
					}
				>
					<HeatMapToolbar
						cameraPerspective={cameraPerspective}
						onToggleCamera={toggleCameraPerspective}
						onZoomIn={() => setZoomLevel((z) => Math.min(z * 1.1, 1.5))}
						onTogglePlay={handleTogglePlayPause}
						onFullscreen={handleFullscreen}
						animationState={animationState}
						sideFilter={sideFilter}
						onToggleSide={toggleSide}
						onRestart={handleRestart}
						spotView={spotView}
						onToggleSpotView={toggleSpotView}
						cameraAngle={cameraAngle}
						onToggleCameraAngle={toggleCameraAngle}
						zoomLevel={zoomLevel}
						onZoomChange={handleZoomChange}
						isRecording={isRecording}
						onToggleRecording={handleToggleRecording}
					/>

					<Canvas
						shadows
						ref={canvasRef}
						className="!absolute inset-0"
						gl={{
							toneMapping: 0,
							antialias: true,
							powerPreference: "high-performance",
							alpha: false,
							stencil: false,
							logarithmicDepthBuffer: true,
						}}
						dpr={[1, 2]}
					>
						<RegionStatsProcessor
							shots={filteredShots}
							onStatsCalculated={handleRegionStatsCalculated}
						/>
						<ambientLight intensity={0.4} />
						<directionalLight
							position={[10, 20, 10]}
							intensity={0.5}
							castShadow
							shadow-mapSize-width={2048}
							shadow-mapSize-height={2048}
							shadow-camera-far={50}
							shadow-camera-left={-20}
							shadow-camera-right={20}
							shadow-camera-top={20}
							shadow-camera-bottom={-20}
						/>

						{/* Camera setup with key for smooth transitions */}
						<group key={`${cameraPerspective}-${viewMode}`}>
							<PerspectiveCamera
								makeDefault
								position={camera.position}
								rotation={camera.rotation}
								fov={camera.fov}
								near={camera.near}
								far={camera.far}
								zoom={camera.zoom * zoomLevel}
							/>
							<OrbitControls
								target={orbitTarget}
								minDistance={3}
								maxDistance={30}
								maxPolarAngle={Math.PI * 0.85}
								enablePan={false}
								enableZoom={false}
								enableRotate={false}
							/>
						</group>

						<BasketballCourt />

						<mesh position={[0, -0.01, 0]} rotation={[-Math.PI / 2, 0, 0]}>
							<planeGeometry args={[1000, 1000]} />
							<meshStandardMaterial
								color="#031422"
								metalness={0}
								roughness={0}
							/>
						</mesh>

						{!spotView && (
							<RegionView
								shots={filteredShots}
								selectedRegion={selectedRegion}
								onRegionClick={handleRegionClick}
							/>
						)}

						{/* Spot View - show individual dots */}
						{spotView &&
							(viewMode === "HEATMAP" ||
								(viewMode === "ANIMATION" && spotView)) &&
							filteredShots &&
							filteredShots.map((shot, index) => {
								if (!shot.location) {
									return null;
								}
								// Normalize to world space (handles both old and new formats)
								const worldLocation = normalizeToWorldSpace(shot.location);
								return (
									<HeatMapDot
										key={`heatmap-${shot.id || index}-${viewMode}-${spotView}`}
										position={[worldLocation.x, 0.1, worldLocation.z]} // World space coordinates
										userData={shot}
									/>
								);
							})}

						{/* Animation Scene - only shown in animation mode */}
						{viewMode === "ANIMATION" && (
							<SharedAnimationScene
								shots={filteredShots}
								animationState={animationState}
								restartKey={restartKey}
								onAnimationComplete={() => setCompletedCount((c) => c + 1)}
								statsAlwaysVisible={false}
								completedCount={completedCount}
							/>
						)}

						<LogoMesh />
					</Canvas>
				</div>

				{initiateRecording && (
					<RecordingScene
						shots={filteredShots}
						onRecordingComplete={handleRecordingComplete}
						zoomLevel={zoomLevel}
					/>
				)}

				{/* Legend and Stats Placeholder */}
				<div className="py-4">
					{/* Legend */}
					{spotView ? (
						<div className="flex items-center justify-center space-x-2 mb-4">
							<div className="flex items-center space-x-1">
								<span className="text-sm text-gray-600">Made</span>
								<div className="w-3 h-3 bg-green-500 rounded-full"></div>
							</div>
							<div className="flex items-center space-x-1">
								<span className="text-sm text-gray-600">Missed</span>
								<div className="w-3 h-3 bg-gray-400 rounded-full flex items-center justify-center text-white text-xs">
									✕
								</div>
							</div>
						</div>
					) : (
						<div className="flex w-full justify-center mb-4 px-4">
							<div className="flex items-center gap-1">
								<span className="text-sm text-gray-600 font-medium pr-4">
									Percentage
								</span>
								<span className="text-sm text-gray-500">Low</span>
								<div
									className="w-4 h-4 border border-color-border-grey-2"
									style={{ background: "#DDE1FF" }}
								/>
								<div
									className="w-4 h-4 border border-color-border-grey-2"
									style={{ background: "#BEC6FF" }}
								/>
								<div
									className="w-4 h-4 border border-color-border-grey-2"
									style={{ background: "#97A4FF" }}
								/>
								<div
									className="w-4 h-4 border border-color-border-grey-2"
									style={{ background: "#6B7BE8" }}
								/>
								<span className="text-sm text-gray-500">High</span>
							</div>
						</div>
					)}

					{/* Stats */}
					<div className="bg-white w-full h-full p-3">
						{selectedRegionDetail && !spotView ? (
							<RegionDetailView
								region={selectedRegionDetail}
								onClose={handleCloseRegionDetail}
							/>
						) : spotView ? (
							<SpotViewBottom {...shootingStats} />
						) : (
							<RegionViewBottom
								shootingStats={shootingStats}
								regionStats={regionStats}
								onRegionClick={handleRegionDetailClick}
							/>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};
